/* eslint-disable sort-exports/sort-exports */
import { atom } from "jotai";
import { z } from "zod";

import { JotaiTool } from "../../index";
import { setUserAuthConfirmationAtom } from "../zap_field_mapping/shared/atoms";

export const ConfirmAuthParams = z.object({
  implementation_id: z
    .string()
    .describe(
      "The app implementation ID with version (e.g., 'GmailCLIAPI@1.5.0')",
    ),
  auth_id: z
    .string()
    .describe("The authentication ID that was created/selected"),
  auth_label: z
    .string()
    .describe("The label/name of the authentication connection"),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Confirming authentication...' or 'Applying connection...'",
    ),
});

export type ConfirmAuthParamsType = z.infer<typeof ConfirmAuthParams>;

export const confirmAuthExecute = async (rawArgs: any): Promise<any> => {
  let params: ConfirmAuthParamsType;
  try {
    params = ConfirmAuthParams.parse(rawArgs || {});
  } catch (error) {
    console.error("[CONFIRM_AUTH] Error parsing arguments:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for confirm_auth. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const { implementation_id, auth_id, auth_label } = params;

  try {
    // Confirm authentication is ready for use
    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(
            {
              implementation_id,
              auth_id: parseInt(auth_id, 10),
              auth_label,
              status: "confirmed",
              confirmed: true,
              message: `✅ Authentication confirmed: "${auth_label}" (ID: ${auth_id}) for ${implementation_id}`,
              next_steps: [
                "Authentication is ready to use in update_zap",
                "Use auth_id in step's authentication_id field",
                "Proceed with field mapping using confirmed authentication",
              ],
              ready_for_field_mapping: true,
            },
            null,
            2,
          ),
        },
      ],
    };
  } catch (error) {
    console.error("[CONFIRM_AUTH] Error in execution:", error);
    return {
      $error: {
        message: `Error confirming authentication: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const confirmAuthDescription = `Confirms and prepares authentication for use in Zap steps after auth creation.

This tool should be used immediately after create_auth succeeds to:
1. Mark the authentication as confirmed and ready
2. Prepare the auth_id for use in update_zap
3. Enable field mapping to proceed

**Usage Pattern:**
1. create_auth completes successfully 
2. Call confirm_auth with the returned auth details
3. Use the returned auth_id in update_zap step's authentication_id field

Parameters:
- implementation_id: Full app identifier with version (e.g., "GmailCLIAPI@1.5.0")
- auth_id: The authentication ID returned from create_auth
- auth_label: The authentication connection name/label

Returns:
- Confirmed authentication details ready for use
- auth_id formatted as number for update_zap
- Confirmation that field mapping can proceed`;

export const confirmAuthAtom = atom<
  JotaiTool<z.infer<typeof ConfirmAuthParams>>
>(() => ({
  name: "confirm_auth",
  description: confirmAuthDescription,
  parameters: ConfirmAuthParams,
  execute: atom(
    null, // read function (not used for write-only atom)
    async (get, set, { args }) => {
      const result = await confirmAuthExecute(args);

      // Update auth state based on the result
      if (result.content?.[0]?.text) {
        try {
          const authData = JSON.parse(result.content[0].text);
          const { implementation_id, auth_id, auth_label, confirmed } =
            authData;

          // Set user confirmation in the auth state
          set(setUserAuthConfirmationAtom, {
            implementation_id,
            auth_id: auth_id.toString(),
            auth_label,
            confirmed: confirmed || false,
          });
        } catch (error) {
          console.error("[CONFIRM_AUTH] Error updating auth state:", error);
        }
      }

      return result;
    },
  ),
}));

confirmAuthAtom.debugLabel = "confirmAuthAtom";

/* eslint-enable sort-exports/sort-exports */
